/* eslint-disable */
/**
 * Brando merchandising — theme-side tracking beacon.
 *
 * DEPLOYMENT (for the theme team):
 *   1. Upload this file as `assets/merch-tracking.js`.
 *   2. Add ONE line to `layout/theme.liquid`, next to the existing main.js
 *      include on line ~153:
 *          {{ "merch-tracking.js" | asset_url | script_tag }}
 *
 *   That is the entire change. This file touches nothing else: it does not
 *   modify `main.js`, does not require a rebuild of the theme's TypeScript
 *   source, and adds no dependency. It can be applied directly to the live
 *   theme. To roll back, remove the one include line — nothing else depends
 *   on it.
 *
 * ─── What it adds, and why the Shopify pixel isn't enough ──────────────────
 * The merchandising API already logs what it SERVED (which products, at which
 * rank), and a Shopify Custom Pixel already reports clicks, cart-adds and
 * purchases. Between them the funnel is covered. This adds the one thing
 * neither can see:
 *
 *   **which products were actually SEEN.**
 *
 * The API serves 68 products per page; a typical shopper scrolls past maybe a
 * dozen. Counting all 68 as "impressions" makes deep positions look like they
 * were shown and ignored, when they were never shown at all. The scoring model
 * corrects for this statistically by fitting expected click-through per
 * position — but that is an assumption. This measures it.
 *
 * It also captures the EXACT grid position of a click. The pixel can only
 * infer position from the referrer's page number.
 *
 * ─── What it deliberately does NOT do ──────────────────────────────────────
 * It does not bypass consent. This is analytics, so it is gated on Shopify's
 * Customer Privacy API exactly like the pixel is, and stays silent for
 * shoppers who decline. It will NOT lift the ~84% consent coverage we measure
 * today — anyone expecting that should stop here.
 */
(function () {
  'use strict';

  // Unit tests load this file in a sandbox with a CommonJS `module`; a
  // browser has none, so this line does nothing on the storefront.
  if (typeof module === 'object' && module && module.exports) module.exports.visitorIdFrom = visitorIdFrom;

  // ─────────────────────── single-initialisation guard ────────────────────
  // If the asset is ever included twice — a duplicated Liquid line, a theme
  // merge, an app that re-injects it — a second copy would attach a second
  // click listener and DOUBLE-COUNT every click. Silent data corruption is
  // worse than no data, so refuse to run twice.
  if (window.__brandoMerchBeacon) return;
  window.__brandoMerchBeacon = true;

  var ENDPOINT =
    'https://shopify-merchandising-api-kj6vlvb7wq-lz.a.run.app/api/events';

  /**
   * `text/plain` is not a mistake. The body is JSON, but declaring it as
   * `application/json` makes the beacon a non-simple CORS request: the browser
   * preflights, and `sendBeacon` then drops the POST silently. That exact bug
   * cost us a day of zero data on the pixel. text/plain is CORS-safelisted, so
   * the request stays simple and actually arrives.
   */
  var CONTENT_TYPE = 'text/plain;charset=UTF-8';

  /** Collection grid scope. `#Collection` comes from collection-template.liquid,
   *  so it is stable and not generated by main.js. Scoping to it keeps
   *  recommendation carousels and "recently viewed" rails out of the data. */
  var SCOPE = '#Collection';

  /** A card must be at least this visible, continuously, for this long. */
  var VISIBLE_RATIO = 0.5;
  var VISIBLE_MS = 500;

  var FLUSH_MS = 5000;
  var MAX_BATCH = 20;

  // ───────────────────────────── consent ──────────────────────────────────

  /**
   * True only when the shopper has allowed analytics. Fails CLOSED: if the
   * privacy API is missing or throws, we send nothing.
   */
  function analyticsAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!(cp && cp.analyticsProcessingAllowed());
    } catch (e) {
      return false;
    }
  }

  // ───────────────────────────── transport ────────────────────────────────

  var queue = [];
  var flushTimer = null;

  function post(payload) {
    try {
      var body = JSON.stringify(payload);
      if (navigator && typeof navigator.sendBeacon === 'function') {
        var queued = navigator.sendBeacon(
          ENDPOINT,
          new Blob([body], { type: CONTENT_TYPE })
        );
        if (queued) return;
      }
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': CONTENT_TYPE },
        body: body,
        keepalive: true
      }).catch(function () {});
    } catch (e) {
      /* analytics must never affect the storefront */
    }
  }

  function flush() {
    flushTimer = null;
    if (!queue.length) return;
    if (!analyticsAllowed()) {
      queue.length = 0;
      return;
    }
    var batch = queue.splice(0, MAX_BATCH);
    // An event queued in the moment between consent and Shopify setting its
    // visitor cookie has no id yet; by flush time it usually does.
    var id = visitorId();
    for (var i = 0; i < batch.length; i++) {
      if (!batch[i].client_id) batch[i].client_id = id;
    }
    post({ events: batch });
    if (queue.length) schedule();
  }

  function schedule() {
    if (flushTimer) return;
    flushTimer = setTimeout(flush, FLUSH_MS);
  }

  function enqueue(event) {
    // Consent is re-checked at flush too, but it MUST be checked here as well.
    // Queuing first and checking only at flush would mean an observation made
    // while consent was denied gets sent retroactively if the shopper accepts
    // during the batching window — the opposite of the fail-closed behaviour
    // this file promises.
    if (!analyticsAllowed()) return;
    queue.push(event);
    if (queue.length >= MAX_BATCH) {
      if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
      }
      flush();
    } else {
      schedule();
    }
  }

  // Getting the queue out before the page goes away is the difference between
  // a usable denominator and a biased one — see the flush() call in the click
  // handler for the measurement that prompted this.
  //
  // `unload` is not in this list on purpose: it never fires on iOS Safari and
  // it disables the back/forward cache. `pagehide` is the modern replacement
  // and fires on real navigations; `visibilitychange` catches tab switches and
  // mobile app-backgrounding, which `pagehide` alone can miss. Both are needed,
  // and flush() is safe to call repeatedly — an empty queue is a no-op.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', function () {
    flush();
  });

  // ───────────────────────────── context ──────────────────────────────────

  /** Set by sections/collection-template.liquid — {id, title, handle, url}. */
  function collectionContext() {
    try {
      var c = window.__collection__;
      if (!c || !c.handle) return null;
      return { handle: String(c.handle).toLowerCase() };
    } catch (e) {
      return null;
    }
  }

  /**
   * Whether the grid is currently narrowed or re-sorted.
   *
   * This matters more than it looks. `positionOf()` reads rank from the LIVE
   * DOM, so a product the API served at rank 40 shows up at rank 3 once the
   * shopper filters. Pooling those two ranks together would make the
   * position-decay curve measure a blend of merchandising position and filter
   * state — i.e. measure nothing useful.
   *
   * We cannot fix that in the theme (main.js renders the cards and does not
   * stamp the served rank on them), so instead we report the context honestly
   * and let the scorer decide: fit the decay curve on unfiltered,
   * default-sorted views only, and treat filtered views as a separate surface.
   */
  function gridContext() {
    try {
      var params = new URLSearchParams(window.location.search);
      var sort = params.get('sort');
      var page = parseInt(params.get('page') || '1', 10);
      if (!isFinite(page) || page < 1) page = 1;
      var filtered = false;
      params.forEach(function (_v, k) {
        if (k !== 'page' && k !== 'sort') filtered = true;
      });
      return { filtered: filtered, sort: sort, page: page };
    } catch (e) {
      // Unknown context is treated as filtered — the conservative choice,
      // since it keeps ambiguous data out of the decay fit.
      return { filtered: true, sort: null, page: 1 };
    }
  }

  function productIdOf(card) {
    var m = /^ProductCard-(\d+)$/.exec(card.id || '');
    return m ? m[1] : null;
  }

  /**
   * 1-indexed rank of a card **within the currently rendered page**.
   *
   * ⚠️ NOT the collection-wide rank. `?page=2` renders only that page's 68
   * cards, so the product whose true rank is 69 sits at DOM index 1 — verified
   * on the live storefront. Reporting that as position 69→1 would corrupt
   * exactly the position-decay data this beacon exists to improve.
   *
   * So every event also carries `page`, and the absolute rank is
   *     (page - 1) * limit + position
   * which is the same arithmetic the server-side impression logger does
   * (src/services/impressions.ts). `limit` deliberately is NOT hardcoded here:
   * it lives in the theme's own API client, and a copy in this file would
   * silently drift the day someone changes the page size.
   */
  function positionOf(card) {
    var scope = document.querySelector(SCOPE);
    if (!scope) return null;
    var cards = scope.querySelectorAll('.product-card');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i] === card) return i + 1;
    }
    // Card is no longer in the grid (replaced mid-timer by a re-render).
    return null;
  }

  // ─────────────────────── visitor id (join key) ──────────────────────────

  /**
   * Shopify's own visitor id, the same id the Custom Pixel reports as
   * `clientId`. Sending it from here too is what lets the scorer join the two
   * producers — and, more importantly, match a click back to the exposure that
   * preceded it. That matching is a hard precondition on scoring (see
   * docs/AUTOMATIC-MERCHANDISING.md, "Events are untrusted"): without it,
   * clicks cannot be tied to a real impression and the endpoint's
   * unauthenticated events cannot be trusted as ranking input.
   *
   * Shopify replaced the `_shopify_y` cookie with `shopify_client_id`, set
   * once analytics consent is given (BRA-1944: 99% of theme events arrived
   * without an id from 9 Oct 2026). On the live store the cookie, the
   * ShopifyAnalytics `uniqToken` and the pixel's `clientId` were the same
   * value. The uniqToken covers the moment between consent and the cookie;
   * before consent it is an all-zero placeholder, never a visitor.
   */
  var NO_VISITOR = /^0{8}-0{4}-0{4}-5000-0{12}$/;
  var VISITOR_COOKIES = [/(?:^|;\s*)shopify_client_id=([^;]+)/, /(?:^|;\s*)_shopify_y=([^;]+)/];

  function visitorIdFrom(cookie, uniqToken) {
    for (var i = 0; i < VISITOR_COOKIES.length; i++) {
      var m = VISITOR_COOKIES[i].exec(cookie || '');
      if (m) return decodeURIComponent(m[1]).slice(0, 100);
    }
    if (typeof uniqToken === 'string' && uniqToken && !NO_VISITOR.test(uniqToken)) {
      return uniqToken.slice(0, 100);
    }
    return null;
  }

  function visitorId() {
    try {
      var lib = window.ShopifyAnalytics && window.ShopifyAnalytics.lib;
      var token;
      try {
        token = lib && lib.user && lib.user().traits().uniqToken;
      } catch (e) {
        token = null;
      }
      return visitorIdFrom(document.cookie, token);
    } catch (e) {
      return null;
    }
  }

  // ──────────────────────── viewport impressions ──────────────────────────

  /**
   * Products already counted THIS SESSION, per collection.
   *
   * Deliberately sessionStorage rather than a plain in-memory map, and this is
   * the whole point: browsing clothes means click a product → back → click
   * another → back, over and over. Each back-navigation is a fresh page load,
   * which would reset an in-memory map and re-count every product still on
   * screen.
   *
   * That over-count is not evenly spread — and that is what makes it
   * dangerous. Back-navigation returns you near where you were, so products at
   * the TOP get re-counted every time while deep products never do (nobody
   * scrolls back to position 60). The denominator would inflate precisely at
   * low positions, flattening the measured position-decay curve and making
   * prime slots look like they underperform. That curve is the backbone of the
   * scoring model, so corrupting it here would quietly corrupt everything
   * downstream.
   *
   * Scoped per collection and cleared with the tab. The trade-off, stated
   * plainly: a shopper who genuinely revisits the same collection hours later
   * in the same tab is counted once. That slightly UNDER-counts, but it does
   * so evenly across positions — an unbiased undercount is far safer than a
   * position-dependent overcount.
   */
  function seenKey(handle) {
    return 'bmb.seen.' + handle;
  }

  function loadSeen(handle) {
    try {
      var raw = window.sessionStorage.getItem(seenKey(handle));
      if (!raw) return Object.create(null);
      var arr = JSON.parse(raw);
      var out = Object.create(null);
      if (Object.prototype.toString.call(arr) === '[object Array]') {
        for (var i = 0; i < arr.length; i++) out[arr[i]] = true;
      }
      return out;
    } catch (e) {
      // Private mode, quota, corrupt value — fall back to per-page-view
      // behaviour rather than breaking. Some duplication beats no data.
      return Object.create(null);
    }
  }

  function persistSeen(handle) {
    try {
      var ids = [];
      for (var k in seen) if (seen[k]) ids.push(k);
      // Bound the entry: a very long session on a 12,000-product collection
      // should not grow this without limit.
      if (ids.length > 2000) ids = ids.slice(ids.length - 2000);
      window.sessionStorage.setItem(seenKey(handle), JSON.stringify(ids));
    } catch (e) {
      /* storage unavailable — in-memory dedup still applies */
    }
  }

  var seen = Object.create(null); // product id → true, once per session
  var timers = Object.create(null); // product id → timeout handle
  var observed = []; // cards currently registered with the IntersectionObserver
  // card element → the product id it was registered FOR. The theme reuses card
  // elements across pages (see observeCards), so the element alone does not say
  // which product it is showing.
  var observedPid = new WeakMap();
  var io = null;
  var mo = null;
  var scopeEl = null;

  function clearTimer(pid) {
    if (timers[pid]) {
      clearTimeout(timers[pid]);
      delete timers[pid];
    }
  }

  function watch(card, pid) {
    io.observe(card);
    observed.push(card);
    observedPid.set(card, pid);
  }

  /**
   * Stop observing a card AND forget it was ever registered. Both halves
   * matter: a card left in `observed` after `io.unobserve()` looks registered
   * to observeCards(), so it is never observed again — which is exactly how
   * page 2+ impressions were lost (see observeCards).
   */
  function unwatch(card) {
    try {
      io.unobserve(card);
    } catch (e) {}
    var i = observed.indexOf(card);
    if (i !== -1) observed.splice(i, 1);
    observedPid.delete(card);
  }

  function onIntersect(entries) {
    var ctx = collectionContext();
    if (!ctx) return;

    entries.forEach(function (entry) {
      var card = entry.target;
      var pid = productIdOf(card);
      if (!pid || seen[pid]) return;

      // `isIntersecting` is TRUE for any overlap at all, including entries
      // emitted when a card drops back BELOW the threshold. Testing it instead
      // of the ratio would count cards that are barely visible — which is
      // precisely the over-counting this beacon exists to eliminate. Test the
      // ratio.
      var visibleEnough = entry.intersectionRatio >= VISIBLE_RATIO;

      if (!visibleEnough) {
        clearTimer(pid);
        return;
      }
      // Already counting down — do not start a second timer, or the handle
      // leaks and an older timer can still fire after the card left view.
      if (timers[pid]) return;

      timers[pid] = setTimeout(function () {
        delete timers[pid];
        if (seen[pid]) return;

        // The element may have been reused for a different product during
        // the delay (pagination and filtering swap ids in place). Counting it
        // would credit the OLD product with a slot it no longer occupies.
        if (productIdOf(card) !== pid) return;

        // Re-check: the card may have scrolled away or been re-rendered
        // during the delay.
        var pos = positionOf(card);
        if (pos === null) return;

        // Consent must be checked BEFORE marking anything as seen, not just
        // before sending. Two things go wrong otherwise:
        //
        //   1. We would write tracking state (the sessionStorage seen-list)
        //      for a shopper who declined analytics — a consent violation in
        //      its own right, regardless of whether an event is sent.
        //   2. Worse: those products stay marked for the rest of the tab
        //      session. A shopper who browses first and accepts the cookie
        //      banner afterwards — an extremely common order of events —
        //      would have every product they had already scrolled past
        //      silently suppressed, permanently.
        //
        // Deliberately do NOT unobserve here: leaving the card observed means
        // it can still be counted if consent is granted later in the session.
        if (!analyticsAllowed()) return;

        seen[pid] = true;
        persistSeen(ctx.handle);
        unwatch(card);

        var grid = gridContext();
        enqueue({
          type: 'impression',
          product_id: pid,
          collection_handle: ctx.handle,
          client_id: visitorId(),
          position: pos,
          page: grid.page,
          filtered: grid.filtered,
          sort: grid.sort,
          // Without this every impression would default to source 'pixel'
          // server-side, making the two producers indistinguishable — which
          // defeats the entire point of collecting this stream separately.
          source: 'theme'
        });
      }, VISIBLE_MS);
    });
  }

  /**
   * Register any not-yet-seen cards, and drop ones no longer in the document.
   *
   * ⚠️ The theme REUSES card elements. Client-side pagination (and filtering)
   * keeps the same 68 `.product-card` nodes and swaps their `ProductCard-{id}`
   * ids and contents in place. So "have I registered this element?" is the
   * wrong question; it must be "have I registered this element FOR THE
   * PRODUCT IT SHOWS NOW?".
   *
   * Getting that wrong cost us most of the page 2+ data. The first version
   * unobserved a card once counted but left it in `observed`. After paging,
   * every slot the shopper had scrolled past on page 1 looked registered and
   * was never observed again. Measured over 20 Aug – 28 Sep 2026: 99.8% of
   * page-1 clicks had a matching impression, page 2+ only 29%. Reproduced on
   * the live store as 4 impressions after paging to page 2 against 64 when
   * loading page 2 directly. Page 2+ CTR read 9–12% when the real figure was
   * nearer 3%.
   */
  function observeCards() {
    if (!io) return;
    var scope = document.querySelector(SCOPE);
    if (!scope) return;

    // Release detached cards so a re-rendered grid doesn't pin the old DOM
    // in memory through the observer.
    var stillObserved = [];
    for (var j = 0; j < observed.length; j++) {
      var prev = observed[j];
      if (!document.contains(prev)) {
        try {
          io.unobserve(prev);
        } catch (e) {}
        clearTimer(observedPid.get(prev) || '');
        observedPid.delete(prev);
      } else {
        stillObserved.push(prev);
      }
    }
    observed = stillObserved;

    var cards = scope.querySelectorAll('.product-card');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var pid = productIdOf(card);
      var registered = observed.indexOf(card) !== -1;

      // Registered for one product, now showing another. Unobserve and let it
      // be re-registered below. Re-observing makes the IntersectionObserver
      // report the card's current state afresh, and that matters: an element
      // whose visibility did not change (a filter applied without scrolling)
      // would otherwise never report again.
      if (registered && observedPid.get(card) !== pid) {
        clearTimer(observedPid.get(card) || '');
        unwatch(card);
        registered = false;
      }

      if (pid && !seen[pid] && !registered) watch(card, pid);
    }
  }

  // Hydration and filter re-renders arrive as bursts of mutations; collapse
  // each burst into a single pass instead of re-scanning per record.
  var rescanQueued = false;
  var lastPage = null;

  function queueRescan() {
    if (rescanQueued) return;
    rescanQueued = true;
    (window.requestAnimationFrame || window.setTimeout)(function () {
      rescanQueued = false;

      // Pagination here is client-side: the URL changes and the grid is
      // replaced without a page load. Flush before adopting the new page so
      // the previous page's pending impressions are attributed to the page
      // they were actually seen on, and so a shopper who pages several times
      // in a row cannot accumulate an unsent backlog that dies with the tab.
      // Bounds impression loss to a single page view.
      var page = gridContext().page;
      if (lastPage !== null && page !== lastPage) flush();
      lastPage = page;

      rebindIfScopeReplaced();
      observeCards();
    }, 0);
  }

  // ───────────────────────────── clicks ───────────────────────────────────

  /**
   * Delegated, on `document`. This is what makes the whole file additive:
   * the cards are rendered by main.js, but we never have to touch it — a
   * listener on document sees clicks on elements it never created, including
   * ones added later by filtering or infinite scroll.
   */
  document.addEventListener(
    'click',
    function (e) {
      try {
        if (!analyticsAllowed()) return;
        var target = e.target;
        if (!target || !target.closest) return;

        var card = target.closest('.product-card');
        if (!card) return;
        if (!card.closest(SCOPE)) return;

        var pid = productIdOf(card);
        var ctx = collectionContext();
        if (!pid || !ctx) return;

        var grid = gridContext();
        // Sent immediately rather than batched: the shopper is navigating
        // away and a queued click may never flush.
        post({
          type: 'click',
          product_id: pid,
          collection_handle: ctx.handle,
          client_id: visitorId(),
          position: positionOf(card),
          page: grid.page,
          filtered: grid.filtered,
          sort: grid.sort,
          source: 'theme'
        });

        // Push any pending IMPRESSIONS out now too, because a click is the
        // most reliable warning we get that this page is about to be replaced.
        //
        // This is not tidiness — it fixes a measurable bias. Clicks are sent
        // immediately while impressions wait up to FLUSH_MS, so a shopper who
        // paginates, scans and clicks inside that window contributes a click
        // with no matching impressions. That is not evenly distributed: it hits
        // people browsing deep pages hardest, because paginating signals intent
        // and those sessions are short and purposeful.
        //
        // Observed before this fix: ~29% of clicks came from page 2+ but only
        // ~3% of impressions did, and for the SAME products the implied CTR on
        // page 2+ ran 2.3x the page-1 rate. A missing denominator inflates
        // exactly the deep-ranked products the model is least able to
        // sanity-check.
        flush();
      } catch (err) {
        /* never interfere with the navigation */
      }
    },
    true // capture, so we run before any handler that stops propagation
  );

  // ───────────────────────────── lifecycle ────────────────────────────────

  /**
   * main.js can replace the whole grid subtree on filter. If `#Collection`
   * itself is swapped, a MutationObserver bound to the old node watches a
   * detached tree forever and we silently stop seeing new cards. Re-bind when
   * the node identity changes.
   */
  function rebindIfScopeReplaced() {
    var current = document.querySelector(SCOPE);
    if (!current || current === scopeEl) return;
    if (mo) {
      try {
        mo.disconnect();
      } catch (e) {}
    }
    scopeEl = current;
    if ('MutationObserver' in window) {
      mo = new MutationObserver(queueRescan);
      // `id` too: a reused card that changes product may only change its id
      // attribute and not add or remove any children. See observeCards.
      mo.observe(scopeEl, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['id']
      });
    }
  }

  /**
   * Watch a STABLE ancestor for #Collection itself being swapped out.
   *
   * Subtle but fatal without it: an observer bound to #Collection never sees
   * its own removal, because that mutation happens in the PARENT. So if a
   * client-side re-render replaces the whole section, queueRescan() is never
   * called, rebindIfScopeReplaced() never runs, and tracking dies silently —
   * the worst failure mode for analytics, since nothing looks broken.
   */
  function watchForScopeReplacement() {
    if (!('MutationObserver' in window) || !document.body) return;
    var rootMo = new MutationObserver(function () {
      if (document.querySelector(SCOPE) !== scopeEl) queueRescan();
    });
    rootMo.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Re-arm when the shopper accepts the cookie banner mid-session.
   *
   * Without this the consent gate above is only half a fix. Declining (or not
   * yet answering) makes the impression timer bail out, and a card that is
   * already sitting still on screen will not produce another
   * IntersectionObserver callback — its ratio never changes. So those products
   * would stay uncounted for the rest of the page view even after consent is
   * granted.
   *
   * Browse-then-accept is a very common order of events, so re-observing
   * everything unseen at that moment is what makes consent-granted sessions
   * complete rather than half-empty. Shopify fires this event from the
   * Customer Privacy API when the banner is answered.
   */
  function watchForConsentChange() {
    try {
      document.addEventListener('visitorConsentCollected', function () {
        if (!analyticsAllowed() || !io) return;

        // Simply calling observeCards() is NOT enough. Those cards are already
        // registered, and IntersectionObserver does not re-emit for an element
        // whose intersection has not changed — a card sitting still on screen
        // would stay silent forever. Detaching and re-attaching forces a fresh
        // initial callback with the element's current ratio, which is what
        // actually re-arms the countdown.
        for (var i = 0; i < observed.length; i++) {
          var card = observed[i];
          var pid = productIdOf(card);
          if (!pid || seen[pid]) continue;
          try {
            io.unobserve(card);
            io.observe(card);
          } catch (e) {}
        }
        observeCards();
      });
    } catch (e) {
      /* no consent API — nothing to re-arm */
    }
  }

  function start() {
    if (!document.querySelector(SCOPE)) return; // not a collection page
    if (!('IntersectionObserver' in window)) return;

    // Restore what this session already counted, so a back-navigation does
    // not re-count everything still on screen.
    var ctx0 = collectionContext();
    if (ctx0) seen = loadSeen(ctx0.handle);
    lastPage = gridContext().page;

    // A single observer with an explicit threshold list: we need callbacks
    // both when a card crosses INTO 50% and when it falls back out, so the
    // countdown can be cancelled.
    io = new IntersectionObserver(onIntersect, { threshold: [0, VISIBLE_RATIO] });

    rebindIfScopeReplaced();
    watchForScopeReplacement();
    watchForConsentChange();
    observeCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
